import { useEffect, useState, useRef, useMemo } from "react";
import { Button, Typography, Tag, Space, Avatar, Tooltip } from "antd";
import moment from "moment";
import { 
  EyeOutlined, 
  UserOutlined, 
  DollarOutlined,
  CalendarOutlined
} from "@ant-design/icons";

import { IOrder } from "../../../redux/@types";
import Table from "../../../shared/components/ui/Table";
import { TableColumns } from "../../../shared/components/ui/Table/types";
import { ordersService, orderStatusService } from "../../../shared/services";
import { decimal } from "../../../shared/utils/Helper";
import { useTableOrders } from "../hooks/useTableOrders";
import { OrderStatusActions } from "./OrderStatusActions";

const { Text } = Typography;

interface DataStatus {
  id: number;
  name: string;
  description: string;
  status_code: string;
  color: string;
  deletedAt: null;
  createdAt: Date;
  updatedAt: Date;
}

export const TableOrders = ({ storeId }: { storeId?: number }) => {
  // const [tableProps, setTableProps] = useState<TableProperties>()
  // const tes = ()=>{
  //   tableProps?.refresh()
  // }

  const [orderStatus, setOrderStatus] = useState<DataStatus[]>([]);
  const tableRefreshRef = useRef<any>(null);

  const getOrderStatus = async () => {
    await orderStatusService
      .find({ query: { $limit: 9999999999999 } })
      .then((res) => {
        setOrderStatus(res.data);
      });
  };

  useEffect(() => {
    getOrderStatus();
  }, []);

  const { changeOrderForUpdate } = useTableOrders();

  const fetchQueryProps = useMemo(
    () => ({
      $sort: { id: -1 } as const,
      ...(storeId != null ? { store_id: storeId } : {}),
    }),
    [storeId]
  );

  const columns: TableColumns<IOrder> = [
    {
      title: "ID",
      width: 5,
      dataIndex: "id",
      key: "id",
      filterType: "filterByID",
      render: (_, item) => {
        return (
          <Space size={4} style={{ background: "#fff7e6", padding: "6px 10px", borderRadius: 6, border: "1px solid #ffe58f" }}>
            <Text strong style={{ color: "#FF6B35", fontSize: 14, fontFamily: "monospace" }}>
              #{item.id}
            </Text>
          </Space>
        );
      },
    },
    // {
    //   title: "Email",
    //   width: 5,
    //   dataIndex: "first_name",
    //   // key: "data_value",
    //   render: (_, item) => {
    //     return (
    //       item.user.first_name
    //     );
    //   },
    //   filterType: "text",
    // },
    // {
    //   title: "Email",
    //   width: 5,
    //   dataIndex: "last_name",
    //   // key: "data_value",
    //   render: (_, item) => {
    //     return (
    //       item.user.last_name
    //     );
    //   },
    //   filterType: "text",
    // },
    {
      title: "Cliente",
      width: 12,
      render: (_, item) => {
        return (
          <Space size={12}>
            <Avatar 
              icon={<UserOutlined />} 
              size={40}
              style={{ 
                backgroundColor: "#fff7e6", 
                color: "#FF6B35",
                border: "1px solid #ffe58f",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
              }}
            />
            <div style={{ minWidth: 0 }}>
              <Text 
                strong 
                style={{ display: "block", fontSize: 14, color: "#262626" }}
                ellipsis={{ tooltip: `${item.user.first_name} ${item.user.last_name}` }}
              >
              {`${item.user.first_name} ${item.user.last_name}`}
              </Text>
              <Text 
                type="secondary" 
                style={{ fontSize: 12 }}
                ellipsis={{ tooltip: item.user.email }}
              >
                {item.user.email}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: "Estado",
      width: 10,
      dataIndex: "order_status_id",
      render: (_, item) => {
        const status = orderStatus?.find(
          (order) => order.id === item.order_status_id
        );
        return (
          <Tag
            color={status?.color}
            style={{
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 600,
              border: "none",
              textAlign: "center",
              minWidth: 100
            }}
          >
            {item.status.name}
          </Tag>
        );
      },
      filters: orderStatus.map((item) => {
        return {
          text: `${item.name}`,
          value: item.id,
        };
      })
    },
    {
      title: "Pago",
      width: 6,
      dataIndex: "payment_method",
      key: "payment_method",
      render: (_, item) => {
        const isOnline = item.payment_method === "online";
        return (
          <Tag 
            color={isOnline ? "orange" : "default"}
            style={{
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 500,
              padding: "4px 8px",
              backgroundColor: isOnline ? "#fff7e6" : "#f5f5f5",
              borderColor: isOnline ? "#ffe58f" : "#d9d9d9",
              color: isOnline ? "#FF6B35" : "#595959",
            }}
          >
            {isOnline ? "Online" : "C. Entrega"}
          </Tag>
        );
      },
    },
    {
      title: "Total",
      width: 6,
      dataIndex: "total_price",
      key: "total_price",
      render: (_, item) => {
        return (
          <Space size={4} style={{ background: "#fff7e6", padding: "6px 10px", borderRadius: 6, border: "1px solid #ffe58f" }}>
            <DollarOutlined style={{ color: "#FF6B35", fontSize: 16 }} />
            <Text strong style={{ fontSize: 14, color: "#FF6B35" }}>
              {decimal(item.total_price)}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Fecha",
      width: 6,
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_, item) => {
        return (
          <Space size={4}>
            <CalendarOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
            <div>
              <Text style={{ display: "block", fontSize: 11 }}>
                {moment(item.createdAt).add("h",5).format("DD/MM/YY")}
              </Text>
              <Text type="secondary" style={{ fontSize: 10 }}>
                {moment(item.createdAt).add("h",5).format("HH:mm")}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      fixed: "right",
      width: 7,
      render: (_, item) => {
        return (
          <Space direction="horizontal" size={4} style={{ width: "100%", justifyContent: "flex-end" }}>
            <OrderStatusActions
              order={item}
              orderStatus={orderStatus}
              onStatusChanged={() => {
                if (tableRefreshRef.current?.refresh) {
                  tableRefreshRef.current.refresh();
                }
              }}
            />
            <Tooltip title="Ver detalle del pedido">
              <Button
                type="primary"
                ghost
                icon={<EyeOutlined style={{ fontSize: 16 }} />}
                onClick={() => changeOrderForUpdate(item)}
                size="middle"
                style={{ 
                  borderRadius: 6,
                  height: 32,
                  width: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  borderColor: "#FF6B35",
                  color: "#FF6B35"
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Table<IOrder>
      columns={columns}
      fetchQueryProps={fetchQueryProps}
      service={ordersService}
      onLoad={(tableProps) => {
        tableRefreshRef.current = tableProps;
      }}
    />
  );
};
